import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpApi from 'i18next-http-backend';

// Translation resources
const resources = {
    en: {
        translation: {
            pricingCalc: {
                headerTitle: "EBM Integrated POS Pricing Calculator",
                headerSubtitle: "Configure your perfect POS system and get an instant price quote",
                headerNotice: "Prefer using the same system without EBM integration? Try our",
                headerNoticeApp: "HovaStore app on Android",
                steps: {
                    eligibility: { title: "Eligibility", desc: "Check if you qualify for free software" },
                    device: { title: "Device Strategy", desc: "Choose your POS setup approach" },
                    hardware: { title: "Hardware", desc: "Select your POS hardware" },
                    plan: { title: "Plan", desc: "Choose your software plan" },
                    users: { title: "Users", desc: "Specify number of users" },
                    summary: { title: "Summary", desc: "Review your configuration" }
                },
                nav: { previous: "Previous", next: "Next", edit: "Edit" },
                hardware: {
                    heading: "Select Your Hardware",
                    subheading: "Choose the devices that make up your POS system",
                    screenHeading: "Screen Device (Required)",
                    allInOne: "All-in-One POS Devices",
                    smartphones: "Smartphones (as POS Screen)",
                    printerHeading: "Printer (Required)",
                    scannerHeading: "Scanner (Optional)",
                    adaptersHeading: "Required Adapters",
                    adaptersTitle: "Adapters Added Automatically",
                    adaptersInfo: "We've added the necessary adapters to connect your devices:",
                    selection: "Your Selection",
                    labelScreen: "Screen:",
                    labelPrinter: "Printer:",
                    labelScanner: "Scanner:",
                    labelAdapters: "Adapters:",
                    itemsCount: "item(s)",
                    builtInPrinter: "Built-in Printer"
                },
                summary: {
                    heading: "Order Summary",
                    subheading: "Review your POS configuration and pricing",
                    config: "Configuration",
                    eligibility: "Software Eligibility:",
                    free: "Free",
                    paidPlan: "Paid Plan",
                    deviceStrategy: "Device Strategy:",
                    usePhone: "Use existing smartphone",
                    dedicatedPos: "Dedicated POS device",
                    hardwareItems: "Hardware Items",
                    noHardware: "No hardware items selected",
                    software: "Software",
                    rdbFree: "Free (RDB eligible)",
                    monthPlan: "month plan",
                    pricingSummary: "Pricing Summary",
                    hardwareTotal: "Hardware Total:",
                    softwareTotal: "Software Total:",
                    additionalUsers: "Additional Users",
                    softwareDiscount: "Software Discount:",
                    grandTotal: "Grand Total:",
                    downloadPdf: "Download PDF",
                    copyLink: "Copy Link",
                    copied: "Copied!",
                    shareWhatsapp: "Share via WhatsApp",
                    nextSteps: "Next Steps",
                    nextStepsInfo: "This is a configuration preview. Contact our sales team to finalize your order and arrange delivery. All prices are in RWF and include local taxes."
                },
                eligibilityStep: {
                    title: "Software Eligibility",
                    question: "Do you qualify for complimentary software based on your RDB registration?",
                    cardTitle: "Startup Free 2 Years Trial Eligibility",
                    cardInfoPrefix: "If your RDB registration is less than 2 years old, you qualify for our Startup Free 2 years trial. The trial is capped at",
                    cardInfoSuffix: ", equivalent to two years of software (after applicable discounts).",
                    yesLabel: "Yes, I qualify",
                    yesDesc: "My RDB registration is less than 2 years old",
                    noLabel: "No, I don't qualify",
                    noDesc: "My RDB registration is 2 years or older",
                    congratsTitle: "Congratulations!",
                    congratsMsg: "You qualify for complimentary HovaStore POS software! You can skip the plan selection step.",
                    planRequiredTitle: "Software Plan Required",
                    planRequiredMsg: "You will need to select a software plan. Choose from 1, 6, 12, or 24-month plans with discounts up to 35%. You may also qualify for a 1-month free trial."
                },
                deviceStep: {
                    title: "Choose Your Device Strategy",
                    subtitle: "Decide how you want to set up your POS system",
                    qExistingPhone: "Do you want to use your existing smartphone as the POS screen?",
                    yesUsePhone: "Yes, use my smartphone",
                    yesUsePhoneDesc: "Use your existing phone as the POS screen. You'll need to add a printer and optionally a scanner.",
                    noDedicated: "No, get a dedicated POS device",
                    noDedicatedDesc: "Choose from all-in-one POS devices or separate screen and printer combinations.",
                    phoneDetailsTitle: "Tell us about your smartphone",
                    phoneDetailsSubtitle: "This helps us recommend compatible accessories",
                    portLabel: "Charging Port Type",
                    portPlaceholder: "Select port type",
                    osLabel: "Operating System",
                    osPlaceholder: "Select OS",
                    otgLabel: "USB OTG Support",
                    btLabel: "Bluetooth Support",
                    unknown: "I don't know",
                    yes: "Yes",
                    no: "No",
                    tipsTitle: "Compatibility Tips",
                    tipBt: "Bluetooth accessories work with any smartphone",
                    tipUsb: "USB accessories require OTG support and compatible adapters",
                    tipAuto: "We'll automatically add required adapters to your cart"
                },
                planStep: {
                    title: "Choose Your Software Plan",
                    subtitle: "Select the plan that best fits your business needs",
                    monthly: "Monthly",
                    yearly: "Yearly",
                    freeTrial: "1 Month Free Trial",
                    popular: "Most Popular",
                    features: "Features",
                    users: "users",
                    support: "Support",
                    analytics: "Analytics",
                    ebmIntegration: "EBM Integration",
                    choosePlan: "Choose Plan",
                    noDiscount: "No discount",
                    basePrice: "Base price:",
                    perMonth: "per month. Longer commitments offer significant savings!"
                },
                userCountStep: {
                    title: "Number of Users",
                    subtitle: "How many people will be using the POS system?",
                    userCount: "Number of users",
                    freeUsers: "First 2 users are free",
                    additionalUsers: "Additional users",
                    perUser: "per user per month"
                },
                validation: {
                    selectScreenPrinter: "Please select both a screen and a printer for your POS setup.",
                    selectPrinter: "Please select a printer for your POS setup.",
                    selectScreen: "Please select a screen for your POS setup.",
                    selectPlan: "Please select a software plan to continue.",
                    enterUserCount: "Please enter the number of users (minimum 1).",
                    selectEligibility: "Please select whether you qualify for complimentary software based on your RDB registration.",
                    selectDeviceStrategy: "Please choose whether to use your existing smartphone or a dedicated POS device.",
                    freeSoftwareNoPlan: "You qualify for complimentary software. No plan selection needed.",
                    completeCurrentStep: "Please complete the current step to continue."
                }
            },
            navbar: {
                home: "Home",
                features: "Features",
                pricing: "Pricing",
                aboutUs: "About Us",
                contact: "Contact",
                learnMore: "Learn More",
                faqs: "FAQs",
                docs: "Docs",
            },
            hero: {
                headline: "Empower Your Business with AI-Driven Solutions & EBM Integration",
                subheading: "Create a smarter digital storefront, manage your inventory, and provide personalized customer experiences with HovaStore's cutting-edge AI tools.",
                ebmIntegration: "HovaStore is now integrated with Electronic Billing Machines (EBM) to streamline VAT compliance and improve tax administration.",
                learnMore: "Learn More",
                altText: "Video showcasing the capabilities of HovaStore",
                pos: 'Request POS',
                badge: "RRA EBM 2.1 integrated POS",
                calculatePrice: "Calculate Your POS Price",
                exploreFeatures: "Explore Features",
                workspace: "HovaStore live workspace",
                liveSales: "Live sales",
                salesLift: "+18% today",
                ebmReady: "EBM ready",
                receiptsSynced: "Receipts synced",
                statEbm: "EBM",
                statCertified: "Certified",
                statMultistore: "Multi-store",
                statReady: "Ready",
                statLive: "Live",
                statAnalytics: "Analytics",
            },
            feature: {
                eyebrow: "HovaStore POS",
                keyFeatures: "Key Features",
                featuresIntro: "Discover the powerful tools that make HovaStore the ultimate solution for your online business.",
                aiRecommendations: "AI Recommendations",
                aiDescription: "Leverage AI to recommend products, boosting sales and improving customer experience.",
                storeManagement: "Effortless Store Management",
                storeDescription: "Easily manage your inventory, process orders, and keep track of everything in one place.",
                analytics: "Powerful Analytics",
                analyticsDescription: "Gain insights into customer behavior and sales trends to make data-driven decisions.",
                ebmIntegration: "EBM Integration",
                ebmDescription: "HovaStore is integrated with Electronic Billing Machines (EBM), helping you streamline VAT compliance and tax reporting.",
                customizable: "Customizable",
                customizableDescription: "Fully customizable features to suit your needs.",
                seamlessIntegration: "Seamless Integration",
                seamlessIntegrationDescription: "Integrate with your existing systems effortlessly.",
                fastSetup: "Fast Setup",
                fastSetupDescription: "Learn More quickly with an easy-to-use setup process.",
                realTimeUpdates: "Real-Time Updates",
                realTimeUpdatesDescription: "Get live updates for all your activities and sales.",
            },
            "pricing": {
                "title": "Plans and Pricing",
                "subtitle": "Choose the plan that fits your needs and scale your business with ease.",
                "plans": {
                    "free": {
                        "name": "Free",
                        "price": "$0",
                        "description": "For personal use",
                        "features": [
                            "Unlimited basic features",
                            "Single user only",
                            "Charged for support",
                            "Charged for analytics"
                        ],
                        "buttonText": "Learn More for free"
                    },
                    "smes": {
                        "name": "SMEs",
                        "price": "$9",
                        "description": "Perfect for small and medium businesses",
                        "features": [
                            "Access to all features",
                            "EBM Integration",
                            "2 users",
                            "Priority support",
                            "Access to Analytics"
                        ],
                        "buttonText": "Upgrade to SMEs",
                        "popular": true
                    },
                    "enterprise": {
                        "name": "Enterprise",
                        "price": "$48",
                        "description": "For larger enterprises",
                        "features": [
                            "Access to all features",
                            "EBM Integration",
                            "Advanced analytics",
                            "Dedicated support",
                            "Many users",
                            "Custom branding options"
                        ],
                        "buttonText": "Choose Enterprise"
                    },
                    "custom": {
                        "name": "Custom",
                        "price": "Custom",
                        "description": "Tailored for your business",
                        "features": [
                            "Everything in Enterprise",
                            "Custom integrations",
                            "Flexible contracts",
                            "Unlimited scalability"
                        ],
                        "buttonText": "Contact Us for Custom Plan"
                    }
                }
            },
            aboutUs: {
                title: "About Us",
                description: "We are HovaStore, a company driven by the desire to provide cutting-edge AI-powered solutions and seamless integrations for e-commerce businesses.",
                mission: {
                    title: "Our Mission",
                    description: "To empower online stores with innovative solutions that simplify operations and enhance customer experiences.",
                },
                values: {
                    title: "Our Values",
                    description: "We value creativity, excellence, and customer-centricity in everything we do.",
                },
                vision: {
                    title: "Our Vision",
                    description: "To be the leading provider of intelligent solutions for businesses looking to elevate their digital presence.",
                },
            },
            download: {
                eyebrow: "Mobile and desktop ready",
                title: "⭐ Finance and accounting made simple, not stressful.",
                description:
                    "HovaStore POS, powered by Hova AI, is designed to simplify record-keeping and give you clear insights into your business performance. With automated tracking and easy-to-use tools, you can focus less on paperwork and more on building a sustainable, successful business.",
                googlePlayText: "Get it on Google Play",
                appStoreText: "Download on the App Store",
                benefitSales: "Sales, inventory and expenses",
                benefitEbm: "RRA EBM compliance",
                benefitReports: "Reports and clear insights",
                benefitShop: "Built for daily shop work",
            },
            testimonials: {
                eyebrow: "Customer voices",
                title: "Trusted by businesses running real counters",
                description: "Teams use HovaStore to keep sales, inventory, receipts, EBM, and reports moving with more confidence.",
                customer: "HovaStore customer",
            },
            contact: {
                title: "GET IN TOUCH",
                footer: "Powered by",
                follow: "Follow us on",
                eyebrow: "Hova AI",
                subtitle: "Talk to us about EBM setup, POS hardware, store migration, or daily operations.",
                location: "LOCATION",
                phone: "PHONE",
                email: "EMAIL",
                marqueePos: "POS",
                marqueeEbm: "EBM",
                marqueeInventory: "Inventory",
                marqueeReports: "Reports",
                marqueeExpenses: "Expenses",
                marqueeMultistore: "Multi-store",
                marqueeReceipts: "Receipts",
                marqueeSupport: "Support",
                marqueeSetup: "Store setup",
                marqueeInsights: "Business insights",
                marqueeCare: "Customer care",
            },
            requestPos: {
                eyebrow: "Request POS",
                title: "Tell us what your shop needs",
                description: "Share your setup details and our team will help you configure the right HovaStore POS workflow.",
                stepBusiness: "Business details",
                stepSetup: "POS setup",
                stepFollowUp: "Team follow-up",
                openForm: "Open request form",
                cardEyebrow: "HovaStore POS setup",
                cardTitle: "Request workspace",
                profileTitle: "Store profile",
                profileDetail: "Name, location, TIN and branch",
                workflowTitle: "Sales workflow",
                workflowDetail: "Mobile, desktop, printer and receipts",
                supportTitle: "Support channel",
                supportDetail: "How our team should reach you",
                nextStep: "Next step",
                modalReady: "Open the modal when you are ready",
                modalTitle: "Request HovaStore POS",
                modalSubtitle: "Fill the setup form and our team will follow up.",
            },
            guide: {
                eyebrow: "Guided setup",
                title: "HovaStore User Guide",
                description: "Detailed instructions to help you get the most out of HovaStore POS.",
                viewFull: "View full guide",
                commonQuestions: "Common questions",
                pdfGuide: "PDF guide",
                workflowTitle: "Learn the full HovaStore workflow",
                workflowDescription: "Open the guide when you need a clear walkthrough for setup, sales, expenses, reports, and everyday POS operations.",
                itemStores: "Create and manage stores",
                itemSales: "Record sales and receipts",
                itemExpenses: "Track expenses and reports",
                itemConfidence: "Use HovaStore POS with confidence",
                openPreview: "Open guide preview",
                modalTitle: "HovaStore User Guides",
                modalSubtitle: "Preview the PDF guide inside HovaStore.",
            },
            faq: {
                eyebrow: "Questions",
                title: "HovaStore FAQ",
                description: "Quick answers about setup, RRA EBM, inventory, receipts, support, and day-to-day POS workflows.",
                supportEyebrow: "Support desk",
                supportTitle: "Need a hand?",
                supportDescription: "If your question is not here, our team can help with migration, setup, printers, EBM settings, and store operations.",
                contactSupport: "Contact support",
            }
        },
    },
    fr: {
        translation: {
            pricingCalc: {
                headerTitle: "Calculateur de tarification POS intégré à l’EBM",
                headerSubtitle: "Configurez votre POS et obtenez un devis instantané",
                headerNotice: "Vous préférez utiliser le même système sans EBM ? Essayez notre",
                headerNoticeApp: "application HovaStore sur Android",
                steps: {
                    eligibility: { title: "Éligibilité", desc: "Vérifiez si vous avez droit au logiciel gratuit" },
                    device: { title: "Stratégie d’appareil", desc: "Choisissez votre approche de configuration POS" },
                    hardware: { title: "Matériel", desc: "Sélectionnez votre matériel POS" },
                    plan: { title: "Plan", desc: "Choisissez votre plan logiciel" },
                    users: { title: "Utilisateurs", desc: "Spécifiez le nombre d’utilisateurs" },
                    summary: { title: "Résumé", desc: "Vérifiez votre configuration" }
                },
                nav: { previous: "Précédent", next: "Suivant", edit: "Modifier" },
                hardware: {
                    heading: "Sélectionnez votre matériel",
                    subheading: "Choisissez les appareils qui composent votre POS",
                    screenHeading: "Appareil écran (Obligatoire)",
                    allInOne: "Appareils POS tout-en-un",
                    smartphones: "Smartphones (comme écran POS)",
                    printerHeading: "Imprimante (Obligatoire)",
                    scannerHeading: "Scanner (Optionnel)",
                    adaptersHeading: "Adaptateurs requis",
                    adaptersTitle: "Adaptateurs ajoutés automatiquement",
                    adaptersInfo: "Nous avons ajouté les adaptateurs nécessaires pour connecter vos appareils :",
                    selection: "Votre sélection",
                    labelScreen: "Écran :",
                    labelPrinter: "Imprimante :",
                    labelScanner: "Scanner :",
                    labelAdapters: "Adaptateurs :",
                    itemsCount: "article(s)",
                    builtInPrinter: "Imprimante intégrée"
                },
                summary: {
                    heading: "Récapitulatif de commande",
                    subheading: "Vérifiez votre configuration et les prix",
                    config: "Configuration",
                    eligibility: "Éligibilité au logiciel :",
                    free: "Gratuit",
                    paidPlan: "Payant",
                    deviceStrategy: "Stratégie d’appareil :",
                    usePhone: "Utiliser un smartphone existant",
                    dedicatedPos: "Appareil POS dédié",
                    hardwareItems: "Articles matériels",
                    noHardware: "Aucun matériel sélectionné",
                    software: "Logiciel",
                    rdbFree: "Gratuit (éligible RDB)",
                    monthPlan: "mois",
                    pricingSummary: "Résumé des prix",
                    hardwareTotal: "Total matériel :",
                    softwareTotal: "Total logiciel :",
                    additionalUsers: "Utilisateurs supplémentaires",
                    softwareDiscount: "Remise logiciel :",
                    grandTotal: "Total général :",
                    downloadPdf: "Télécharger le PDF",
                    copyLink: "Copier le lien",
                    copied: "Copié !",
                    shareWhatsapp: "Partager via WhatsApp",
                    nextSteps: "Étapes suivantes",
                    nextStepsInfo: "Ceci est un aperçu de configuration. Contactez nos ventes pour finaliser la commande et la livraison. Tous les prix sont en RWF et incluent les taxes locales."
                },
                eligibilityStep: {
                    title: "Éligibilité au logiciel",
                    question: "Êtes-vous éligible au logiciel gratuit selon votre enregistrement RDB ?",
                    cardTitle: "Éligibilité à l'essai gratuit de 2 ans pour les startups",
                    cardInfoPrefix: "Si votre enregistrement RDB a moins de 2 ans, vous êtes éligible à notre essai gratuit de 2 ans. L'essai est plafonné à",
                    cardInfoSuffix: ", équivalent à deux ans de logiciel (après remises).",
                    yesLabel: "Oui, je suis éligible",
                    yesDesc: "Mon enregistrement RDB a moins de 2 ans",
                    noLabel: "Non, je ne suis pas éligible",
                    noDesc: "Mon enregistrement RDB a 2 ans ou plus",
                    congratsTitle: "Félicitations !",
                    congratsMsg: "Vous bénéficiez du logiciel HovaStore POS gratuitement ! Vous pouvez ignorer l'étape de sélection du plan.",
                    planRequiredTitle: "Plan logiciel requis",
                    planRequiredMsg: "Vous devrez sélectionner un plan logiciel. Choisissez 1, 6, 12 ou 24 mois avec jusqu'à 35% de remise. Vous pouvez aussi bénéficier d'un essai gratuit d'un mois."
                },
                deviceStep: {
                    title: "Choisissez votre stratégie d'appareil",
                    subtitle: "Décidez comment configurer votre système POS",
                    qExistingPhone: "Souhaitez-vous utiliser votre smartphone existant comme écran POS ?",
                    yesUsePhone: "Oui, utiliser mon smartphone",
                    yesUsePhoneDesc: "Utilisez votre téléphone comme écran POS. Vous devrez ajouter une imprimante et éventuellement un scanner.",
                    noDedicated: "Non, obtenir un appareil POS dédié",
                    noDedicatedDesc: "Choisissez des POS tout-en-un ou des combinaisons écran + imprimante.",
                    phoneDetailsTitle: "Parlez-nous de votre smartphone",
                    phoneDetailsSubtitle: "Cela nous aide à recommander des accessoires compatibles",
                    portLabel: "Type de port de charge",
                    portPlaceholder: "Sélectionnez le type de port",
                    osLabel: "Système d'exploitation",
                    osPlaceholder: "Sélectionnez l'OS",
                    otgLabel: "Prise en charge USB OTG",
                    btLabel: "Prise en charge Bluetooth",
                    unknown: "Je ne sais pas",
                    yes: "Oui",
                    no: "Non",
                    tipsTitle: "Conseils de compatibilité",
                    tipBt: "Les accessoires Bluetooth fonctionnent avec tout smartphone",
                    tipUsb: "Les accessoires USB nécessitent l'OTG et des adaptateurs compatibles",
                    tipAuto: "Nous ajouterons automatiquement les adaptateurs requis à votre panier"
                },
                planStep: {
                    title: "Choisissez votre plan logiciel",
                    subtitle: "Sélectionnez le plan qui correspond le mieux à vos besoins",
                    monthly: "Mensuel",
                    yearly: "Annuel",
                    freeTrial: "Essai gratuit d'1 mois",
                    popular: "Le plus populaire",
                    features: "Fonctionnalités",
                    users: "utilisateurs",
                    support: "Support",
                    analytics: "Analyses",
                    ebmIntegration: "Intégration EBM",
                    choosePlan: "Choisir le plan",
                    noDiscount: "Aucune remise",
                    basePrice: "Prix de base :",
                    perMonth: "par mois. Les engagements plus longs offrent des économies importantes !"
                },
                userCountStep: {
                    title: "Nombre d'utilisateurs",
                    subtitle: "Combien de personnes utiliseront le système POS ?",
                    userCount: "Nombre d'utilisateurs",
                    freeUsers: "Les 2 premiers utilisateurs sont gratuits",
                    additionalUsers: "Utilisateurs supplémentaires",
                    perUser: "par utilisateur par mois"
                },
                validation: {
                    selectScreenPrinter: "Veuillez sélectionner un écran et une imprimante pour votre configuration POS.",
                    selectPrinter: "Veuillez sélectionner une imprimante pour votre configuration POS.",
                    selectScreen: "Veuillez sélectionner un écran pour votre configuration POS.",
                    selectPlan: "Veuillez sélectionner un plan logiciel pour continuer.",
                    enterUserCount: "Veuillez saisir le nombre d'utilisateurs (minimum 1).",
                    selectEligibility: "Veuillez sélectionner si vous êtes éligible au logiciel gratuit selon votre enregistrement RDB.",
                    selectDeviceStrategy: "Veuillez choisir d'utiliser votre smartphone existant ou un appareil POS dédié.",
                    freeSoftwareNoPlan: "Vous êtes éligible au logiciel gratuit. Aucune sélection de plan nécessaire.",
                    completeCurrentStep: "Veuillez terminer l'étape actuelle pour continuer."
                }
            },
            navbar: {
                home: "Accueil",
                features: "Caractéristiques",
                pricing: "Tarifs",
                aboutUs: "À propos de nous",
                contact: "Contact",
                learnMore: "Apprendre encore plus",
                faqs: "FAQs",
                docs: "Docs",
            },
            hero: {
                headline: "Faites évoluer votre entreprise avec des solutions basées sur l'IA et une intégration EBM",
                subheading: "Créez une vitrine numérique intelligente, gérez votre inventaire et offrez des expériences client personnalisées grâce aux outils avancés d'HovaStore.",
                ebmIntegration: "HovaStore est désormais intégré aux Machines de Facturation Électronique (EBM) pour simplifier la conformité à la TVA et améliorer l'administration fiscale.",
                learnMore: "Apprendre encore plus",
                altText: "Vidéo montrant les capacités de HovaStore",
                pos: 'Demande de POS',
                badge: "POS intégré à RRA EBM 2.1",
                calculatePrice: "Calculer le prix de votre POS",
                exploreFeatures: "Découvrir les fonctionnalités",
                workspace: "Espace de travail HovaStore en direct",
                liveSales: "Ventes en direct",
                salesLift: "+18% aujourd’hui",
                ebmReady: "EBM prêt",
                receiptsSynced: "Reçus synchronisés",
                statEbm: "EBM",
                statCertified: "Certifié",
                statMultistore: "Multi-magasins",
                statReady: "Prêt",
                statLive: "En direct",
                statAnalytics: "Analyses",
            },
            feature: {
                eyebrow: "HovaStore POS",
                keyFeatures: "Caractéristiques clés",
                featuresIntro: "Découvrez les outils puissants qui font de HovaStore la solution ultime pour votre entreprise en ligne.",
                aiRecommendations: "Recommandations IA",
                aiDescription: "Exploitez l'IA pour recommander des produits, stimuler les ventes et améliorer l'expérience client.",
                storeManagement: "Gestion de magasin facile",
                storeDescription: "Gérez facilement votre inventaire, traitez les commandes et suivez tout en un seul endroit.",
                analytics: "Analytique puissante",
                analyticsDescription: "Obtenez des informations sur le comportement des clients et les tendances des ventes pour prendre des décisions basées sur les données.",
                ebmIntegration: "Intégration EBM",
                ebmDescription: "HovaStore est intégré avec les Machines de Facturation Électronique (EBM), facilitant la conformité à la TVA et la déclaration fiscale.",
                customizable: "Personnalisable",
                customizableDescription: "Des fonctionnalités entièrement personnalisables pour répondre à vos besoins.",
                seamlessIntegration: "Intégration transparente",
                seamlessIntegrationDescription: "Intégrez facilement vos systèmes existants.",
                fastSetup: "Installation rapide",
                fastSetupDescription: "Commencez rapidement grâce à un processus de configuration facile.",
                realTimeUpdates: "Mises à jour en temps réel",
                realTimeUpdatesDescription: "Obtenez des mises à jour en direct pour toutes vos activités et ventes.",
            },
            "pricing": {
                "title": "Plans et tarifs",
                "subtitle": "Choisissez le plan qui correspond à vos besoins et développez votre entreprise en toute simplicité.",
                "plans": {
                    "free": {
                        "name": "Gratuit",
                        "price": "$0",
                        "description": "Pour un usage personnel",
                        "features": [
                            "Fonctionnalités de base illimitées",
                            "Un seul utilisateur",
                            "Support facturé",
                            "Analyses facturées"
                        ],
                        "buttonText": "Commencer gratuitement"
                    },
                    "smes": {
                        "name": "SMEs",
                        "price": "$9",
                        "description": "Parfait pour les petites et moyennes entreprises",
                        "features": [
                            "Accès à toutes les fonctionnalités",
                            "Intégration EBM",
                            "2 utilisateurs",
                            "Support prioritaire",
                            "Accès aux analyses"
                        ],
                        "buttonText": "Passer au plan SMEs",
                        "popular": true
                    },
                    "enterprise": {
                        "name": "Entreprise",
                        "price": "$48",
                        "description": "Pour les grandes entreprises",
                        "features": [
                            "Accès à toutes les fonctionnalités",
                            "Intégration EBM",
                            "Analyses avancées",
                            "Support dédié",
                            "Nombreux utilisateurs",
                            "Options de personnalisation de marque"
                        ],
                        "buttonText": "Choisir Entreprise"
                    },
                    "custom": {
                        "name": "Personnalisé",
                        "price": "Personnalisé",
                        "description": "Adapté à votre entreprise",
                        "features": [
                            "Tout dans Entreprise",
                            "Intégrations personnalisées",
                            "Contrats flexibles",
                            "Évolutivité illimitée"
                        ],
                        "buttonText": "Contactez-nous pour un plan personnalisé"
                    }
                }
            },
            aboutUs: {
                title: "À propos de nous",
                description: "Nous sommes HovaStore, une entreprise animée par le désir de fournir des solutions de pointe alimentées par l'IA et des intégrations transparentes pour les entreprises de commerce électronique.",
                mission: {
                    title: "Notre mission",
                    description: "Permettre aux boutiques en ligne de disposer de solutions innovantes qui simplifient les opérations et améliorent l'expérience client.",
                },
                values: {
                    title: "Nos valeurs",
                    description: "Nous valorisons la créativité, l'excellence et l'orientation client dans tout ce que nous faisons.",
                },
                vision: {
                    title: "Notre vision",
                    description: "Être le fournisseur leader de solutions intelligentes pour les entreprises cherchant à améliorer leur présence numérique.",
                },
            },
            download: {
                eyebrow: "Prêt pour mobile et ordinateur",
                title: "⭐ La finance et la comptabilité, simples et sans stress.",
                description:
                    "HovaStore POS, propulsé par Hova AI, est conçu pour simplifier la tenue de vos registres et vous offrir une vision claire de la performance de votre entreprise. Grâce au suivi automatisé et à des outils simples d’utilisation, vous pouvez passer moins de temps sur la paperasse et vous concentrer sur la construction d’une entreprise durable et prospère.",
                googlePlayText: "Obtenez-le sur Google Play",
                appStoreText: "Téléchargez sur l'App Store",
                benefitSales: "Ventes, inventaire et dépenses",
                benefitEbm: "Conformité RRA EBM",
                benefitReports: "Rapports et informations claires",
                benefitShop: "Conçu pour le travail quotidien en magasin",
            },
            testimonials: {
                eyebrow: "Voix des clients",
                title: "Adopté par des entreprises aux comptoirs actifs",
                description: "Les équipes utilisent HovaStore pour mieux gérer ventes, inventaire, reçus, EBM et rapports.",
                customer: "Client HovaStore",
            },
            contact: {
                title: "Contactez-nous",
                footer: "Alimenté par",
                follow: "Suivez-nous sur",
                eyebrow: "Hova AI",
                subtitle: "Parlez-nous de la configuration EBM, du matériel POS, de la migration de magasin ou des opérations quotidiennes.",
                location: "ADRESSE",
                phone: "TÉLÉPHONE",
                email: "E-MAIL",
                marqueePos: "POS",
                marqueeEbm: "EBM",
                marqueeInventory: "Inventaire",
                marqueeReports: "Rapports",
                marqueeExpenses: "Dépenses",
                marqueeMultistore: "Multi-magasins",
                marqueeReceipts: "Reçus",
                marqueeSupport: "Support",
                marqueeSetup: "Configuration magasin",
                marqueeInsights: "Informations business",
                marqueeCare: "Service client",
            },
            requestPos: {
                eyebrow: "Demande de POS",
                title: "Dites-nous ce dont votre boutique a besoin",
                description: "Partagez vos besoins et notre équipe vous aidera à configurer le bon flux HovaStore POS.",
                stepBusiness: "Informations entreprise",
                stepSetup: "Configuration POS",
                stepFollowUp: "Suivi par l’équipe",
                openForm: "Ouvrir le formulaire",
                cardEyebrow: "Configuration HovaStore POS",
                cardTitle: "Espace de demande",
                profileTitle: "Profil du magasin",
                profileDetail: "Nom, adresse, TIN et branche",
                workflowTitle: "Flux de vente",
                workflowDetail: "Mobile, ordinateur, imprimante et reçus",
                supportTitle: "Canal de support",
                supportDetail: "Comment notre équipe doit vous contacter",
                nextStep: "Prochaine étape",
                modalReady: "Ouvrez le formulaire quand vous êtes prêt",
                modalTitle: "Demander HovaStore POS",
                modalSubtitle: "Remplissez le formulaire et notre équipe vous contactera.",
            },
            guide: {
                eyebrow: "Configuration guidée",
                title: "Guide utilisateur HovaStore",
                description: "Des instructions détaillées pour tirer le meilleur parti de HovaStore POS.",
                viewFull: "Voir le guide complet",
                commonQuestions: "Questions fréquentes",
                pdfGuide: "Guide PDF",
                workflowTitle: "Apprenez tout le flux HovaStore",
                workflowDescription: "Ouvrez le guide pour une explication claire de la configuration, des ventes, des dépenses, des rapports et des opérations POS.",
                itemStores: "Créer et gérer des magasins",
                itemSales: "Enregistrer ventes et reçus",
                itemExpenses: "Suivre dépenses et rapports",
                itemConfidence: "Utiliser HovaStore POS avec confiance",
                openPreview: "Ouvrir l’aperçu du guide",
                modalTitle: "Guides utilisateur HovaStore",
                modalSubtitle: "Prévisualisez le guide PDF dans HovaStore.",
            },
            faq: {
                eyebrow: "Questions",
                title: "FAQ HovaStore",
                description: "Réponses rapides sur la configuration, RRA EBM, l’inventaire, les reçus, le support et les opérations POS quotidiennes.",
                supportEyebrow: "Service support",
                supportTitle: "Besoin d’aide ?",
                supportDescription: "Si votre question n’est pas ici, notre équipe peut vous aider pour la migration, la configuration, les imprimantes, EBM et les opérations.",
                contactSupport: "Contacter le support",
            }
        },
    },
    rw: {
        translation: {
            pricingCalc: {
                headerTitle: "Kalkirateri y'ibiciro bya POS yinjijwe na EBM",
                headerSubtitle: "Shyiraho POS yawe ubone igiciro ako kanya",
                headerNotice: "Ushaka sisiteme imwe idafite EBM? Gerageza",
                headerNoticeApp: "porogaramu ya HovaStore kuri Android",
                steps: {
                    eligibility: { title: "Ukwemererwa", desc: "Reba niba wemerewe software y'ubuntu" },
                    device: { title: "Uburyo bwa Device", desc: "Hitamo uko ushyiraho POS" },
                    hardware: { title: "Ibikoresho", desc: "Hitamo ibikoresho bya POS" },
                    plan: { title: "Plani", desc: "Hitamo plani ya software" },
                    users: { title: "Abakoresha", desc: "Andika umubare w’abakoresha" },
                    summary: { title: "Incamake", desc: "Suzuma ibyo wahisemo" }
                },
                nav: { previous: "Inyuma", next: "Ibikurikira", edit: "Hindura" },
                hardware: {
                    heading: "Hitamo Ibikoresho",
                    subheading: "Hitamo ibikoresho bigize POS yawe",
                    screenHeading: "Ekrani (Byanze bikunze)",
                    allInOne: "POS byose-muri-bimwe",
                    smartphones: "Telefoni (nk’Ekrani ya POS)",
                    printerHeading: "Mucapyi (Byanze bikunze)",
                    scannerHeading: "Sikanneri (Ku bushake)",
                    adaptersHeading: "Adaptateri zikenewe",
                    adaptersTitle: "Ziyongerwaho byikora",
                    adaptersInfo: "Twongeyemo adaptateri zikenewe zo guhuza ibikoresho byawe:",
                    selection: "Ibyo wahisemo",
                    labelScreen: "Ekrani:",
                    labelPrinter: "Mucapyi:",
                    labelScanner: "Sikanneri:",
                    labelAdapters: "Adaptateri:",
                    itemsCount: "ibikoresho",
                    builtInPrinter: "Mucapyi irimo"
                },
                summary: {
                    heading: "Incamake y'Itumiza",
                    subheading: "Suzuma igenamiterere na biciro",
                    config: "Igenamiterere",
                    eligibility: "Ukwemererwa Software:",
                    free: "Ubusa",
                    paidPlan: "Plani yishyurwa",
                    deviceStrategy: "Uburyo bwa Device:",
                    usePhone: "Koresha telefoni usanganywe",
                    dedicatedPos: "POS yihariye",
                    hardwareItems: "Ibikoresho",
                    noHardware: "Nta gikoresho wahisemo",
                    software: "Software",
                    rdbFree: "Ubusa (RDB yemeje)",
                    monthPlan: "amezi",
                    pricingSummary: "Incamake y'ibiciro",
                    hardwareTotal: "Igiteranyo cy'ibikoresho:",
                    softwareTotal: "Igiteranyo cya software:",
                    additionalUsers: "Abakoresha b’inyongera",
                    softwareDiscount: "Igabanywa rya software:",
                    grandTotal: "Igiteranyo cyose:",
                    downloadPdf: "Kuramo PDF",
                    copyLink: "Koporora Link",
                    copied: "Byakoporowe!",
                    shareWhatsapp: "Sangiza kuri WhatsApp",
                    nextSteps: "Intambwe zikurikiraho",
                    nextStepsInfo: "Ibi ni ibigaragaza igenamiterere. Hamagara itsinda ryacu ry’icuruza kurangiza no gutegura gutanga. Ibiciro byose ni mu RWF kandi birimo imisoro y’imbere mu gihugu."
                },
                eligibilityStep: {
                    title: "Ukwemererwa Software",
                    question: "Wemerewe software y'ubuntu hashingiwe kuri RDB yawe?",
                    cardTitle: "Startup Free imyaka 2",
                    cardInfoPrefix: "Niba RDB yawe iri munsi y'imyaka 2, wemerewe igerageza ry’imyaka 2 ku buntu. Igerageza rigarukira kuri",
                    cardInfoSuffix: ", ringana n’imyaka ibiri ya software (nyuma y’igabanywa rikwiye).",
                    yesLabel: "Yego, ndemerewe",
                    yesDesc: "RDB yanjye iri munsi y’imyaka 2",
                    noLabel: "Oya, sindemerewe",
                    noDesc: "RDB yanjye ifite imyaka 2 cyangwa irenga",
                    congratsTitle: "Turagushimiye!",
                    congratsMsg: "Wemerewe HovaStore POS ku buntu! Ushobora gusimbuka guhitamo plani.",
                    planRequiredTitle: "Plani ya Software irakenewe",
                    planRequiredMsg: "Ugomba guhitamo plani: 1, 6, 12, cyangwa 24 amezi, igabanywa kugeza kuri 35%. Ushobora no kubona igerageza ry’ukwezi 1 ku buntu."
                },
                deviceStep: {
                    title: "Hitamo Uburyo bwa Device",
                    subtitle: "Fata umwanzuro uko ushyiraho POS yawe",
                    qExistingPhone: "Ukoresha telefoni usanganywe nk’ekrani ya POS?",
                    yesUsePhone: "Yego, nkoreshe telefoni",
                    yesUsePhoneDesc: "Koresha telefoni nk’ekrani ya POS. Ongeraho mucapyi na sikanneri (ku bushake).",
                    noDedicated: "Oya, nkeneye POS yihariye",
                    noDedicatedDesc: "Hitamo POS byose-muri-bimwe cyangwa Ekrani + Mucapyi.",
                    phoneDetailsTitle: "Amakuru ya telefoni yawe",
                    phoneDetailsSubtitle: "Bituma tugira inama z’ibikoresho bihuye",
                    portLabel: "Ubwoko bwa port yo kwishyuza",
                    portPlaceholder: "Hitamo port",
                    osLabel: "OS",
                    osPlaceholder: "Hitamo OS",
                    otgLabel: "USB OTG irakorera",
                    btLabel: "Bluetooth irahari",
                    unknown: "Sinzi",
                    yes: "Yego",
                    no: "Oya",
                    tipsTitle: "Inama z'ubushobozi bwo guhuza",
                    tipBt: "Bluetooth ikora kuri telefoni yose",
                    tipUsb: "USB isaba OTG n’adaptateri zihuye",
                    tipAuto: "Adaptateri zikenewe zongerwaho byikora"
                },
                planStep: {
                    title: "Hitamo Plani ya Software",
                    subtitle: "Hitamo plani ijyanye n'ibyo ukeneye",
                    monthly: "Buri kwezi",
                    yearly: "Buri mwaka",
                    freeTrial: "Igerageza ry’ukwezi 1",
                    popular: "Bikunzwe",
                    features: "Ibiranga",
                    users: "abakoresha",
                    support: "Ubufasha",
                    analytics: "Isesengura",
                    ebmIntegration: "Kwihuza na EBM",
                    choosePlan: "Hitamo Plani",
                    noDiscount: "Nta kugabanuka",
                    basePrice: "Igiciro fatizo:",
                    perMonth: "ku kwezi. Isezerano rirerire rigabanya igiciro!"
                },
                userCountStep: {
                    title: "Umubare w’Abakoresha",
                    subtitle: "Ni bangahe bazakoresha POS?",
                    userCount: "Umubare w’abakoresha",
                    freeUsers: "Abambere 2 ni ubuntu",
                    additionalUsers: "Abakoresha b’inyongera",
                    perUser: "ku mukoresha/ukwezi"
                },
                validation: {
                    selectScreenPrinter: "Hitamo ekrani na mucapyi kuri POS yawe.",
                    selectPrinter: "Hitamo mucapyi kuri POS yawe.",
                    selectScreen: "Hitamo ekrani kuri POS yawe.",
                    selectPlan: "Hitamo plani ya software kugira ngo ukomeze.",
                    enterUserCount: "Shyiramo umubare w’abakoresha (ntarengwa 1).",
                    selectEligibility: "Hitamo niba wemerewe software y’ubuntu hashingiwe kuri RDB.",
                    selectDeviceStrategy: "Hitamo hagati ya telefoni cyangwa POS yihariye.",
                    freeSoftwareNoPlan: "Wemerewe software y’ubuntu. Guhitamo plani ntibikenewe.",
                    completeCurrentStep: "Banze wuzuza iyi ntambwe kugira ngo ukomeze."
                }
            },
            navbar: {
                home: "Ahabanza",
                features: "Ibiranga",
                pricing: "Ibiciro",
                aboutUs: "Ibyacu",
                contact: "Tuvugishe",
                learnMore: "Menya byinshi",
                faqs: "Ibibazo",
                docs: "Docs"
            },
            hero: {
                headline: "Fasha ubucuruzi bwawe n’AI & EBM",
                subheading: "Shyiraho iduka rigezweho, tegeka stok, utange ubunararibonye bwihariye ukoresheje HovaStore.",
                ebmIntegration: "HovaStore ihujwe na EBM korohereza TVA no gutanga raporo.",
                learnMore: "Menya byinshi",
                altText: "Video igaragaza ubushobozi bwa HovaStore",
                pos: "Saba POS",
                badge: "POS ihujwe na RRA EBM 2.1",
                calculatePrice: "Bara igiciro cya POS",
                exploreFeatures: "Reba ibiranga",
                workspace: "Akazi ka HovaStore live",
                liveSales: "Ibyagurishijwe live",
                salesLift: "+18% uyu munsi",
                ebmReady: "EBM yiteguye",
                receiptsSynced: "Inyemezabuguzi zasinyijwe",
                statEbm: "EBM",
                statCertified: "Yemejwe",
                statMultistore: "Amaduka menshi",
                statReady: "Yiteguye",
                statLive: "Live",
                statAnalytics: "Isesengura"
            },
            feature: {
                eyebrow: "HovaStore POS",
                keyFeatures: "Iby'ingenzi",
                featuresIntro: "Menya ibikoresho bituma HovaStore iba igisubizo cyiza.",
                aiRecommendations: "Inama za AI",
                aiDescription: "Guhitiramo ibicuruzwa hifashishijwe AI kugira ngo wongere kugurisha.",
                storeManagement: "Kuyobora iduka byoroshye",
                storeDescription: "Tegura stok, amatike n’ibindi ahantu hamwe.",
                analytics: "Isesengura rikomeye",
                analyticsDescription: "Menya imyitwarire y’abakiriya n’imigendekere y’igurisha.",
                ebmIntegration: "Kwihuza na EBM",
                ebmDescription: "Byoroshya kubahiriza TVA no gutanga imisoro.",
                customizable: "Yihindurwamo",
                customizableDescription: "Hindura uko ukeneye.",
                seamlessIntegration: "Kwihuza neza",
                seamlessIntegrationDescription: "Yinjira muri sisiteme usanganywe byoroshye.",
                fastSetup: "Gushyiraho vuba",
                fastSetupDescription: "Tangiza vuba ukoresheje ishyiraho ryoroheje.",
                realTimeUpdates: "Amakuru y’ako kanya",
                realTimeUpdatesDescription: "Menya iby’ibikorwa n’igurisha mu gihe nyacyo."
            },
            pricing: {
                title: "Plani n'Ibiciro",
                subtitle: "Hitamo plani ijyanye n'ibyo ukeneye kandi wiyongere utuje.",
                plans: {
                    free: {
                        name: "Ubusa",
                        price: "$0",
                        description: "Ku muntu ku giti cye",
                        features: [
                            "Ibiranga by’ibanze bitagira imipaka",
                            "Umukoresha umwe",
                            "Ubufasha burishyurwa",
                            "Isesengura ririshyurwa"
                        ],
                        buttonText: "Tangira ku buntu"
                    },
                    smes: {
                        name: "SMEs",
                        price: "$9",
                        description: "Byiza ku SMEs",
                        features: [
                            "Kwemererwa byose",
                            "Kwihuza na EBM",
                            "Abakoresha 2",
                            "Ubufasha buhuta",
                            "Kwinjira ku isesengura"
                        ],
                        buttonText: "Hindura ujye kuri SMEs",
                        popular: true
                    },
                    enterprise: {
                        name: "Isosiyete",
                        price: "$48",
                        description: "Ku bigo binini",
                        features: [
                            "Kwemererwa byose",
                            "Kwihuza na EBM",
                            "Isesengura rihanitse",
                            "Ubufasha bwitayeho",
                            "Abakoresha benshi",
                            "Guhindura isura (branding)"
                        ],
                        buttonText: "Hitamo Isosiyete"
                    },
                    custom: {
                        name: "Byihariye",
                        price: "Byihariye",
                        description: "Bihuye n’ubucuruzi bwawe",
                        features: [
                            "Byose byo muri Isosiyete",
                            "Kwihuza kwihariye",
                            "Amasezerano yoroheje",
                            "Gukwira uko ushaka"
                        ],
                        buttonText: "Tuvugishe kuri plani yihariye"
                    }
                }
            },
            aboutUs: {
                title: "Ibyacu",
                description: "HovaStore itanga ibisubizo bishingiye kuri AI n’ihuriro ryoroheje ku bacuruzi.",
                mission: {
                    title: "Intego yacu",
                    description: "Gufasha amaduka ibisubizo byoroshya imirimo n’uburambe bw’abakiriya."
                },
                values: {
                    title: "Indangagaciro zacu",
                    description: "Dushyira imbere guhanga udushya, gukora neza, no gukunda umukiriya."
                },
                vision: {
                    title: "Icyerecyezo cyacu",
                    description: "Kuba umutanga wa mbere w'ibisubizo by’ubwenge ku bigo bizamura ubuzima bw’ikoranabuhanga ryabyo."
                }
            },
            download: {
                eyebrow: "Ikora kuri telefoni na mudasobwa",
                title: "⭐ Imari n'ibaruramari byoroshye, atari umuzigo.",
                description:
                    "HovaStore POS, ishingiye kuri Hova AI, igufasha kubika neza no kubona ishusho isobanutse y’imikorere y’ubucuruzi bwawe. Ukoresheje gukusanya byikora n’ibikoresho byoroshye, wibande ku kubaka ubucuruzi burambye.",
                googlePlayText: "Bibone kuri Google Play",
                appStoreText: "Bikure kuri App Store",
                benefitSales: "Ibyagurishijwe, stock n’ibisohoka",
                benefitEbm: "Kubahiriza RRA EBM",
                benefitReports: "Raporo n’amakuru asobanutse",
                benefitShop: "Yubakiwe akazi ka buri munsi mu iduka"
            },
            testimonials: {
                eyebrow: "Ibyo abakiriya bavuga",
                title: "Yizewe n’ubucuruzi bukora buri munsi",
                description: "Amakipe akoresha HovaStore kugira ngo agenzure neza igurisha, stock, inyemezabuguzi, EBM na raporo.",
                customer: "Umukiriya wa HovaStore"
            },
            contact: {
                title: "TWANDIKIRE",
                footer: "Byongerwamo imbaraga na",
                follow: "Dukurikire kuri",
                eyebrow: "Hova AI",
                subtitle: "Tubwire ku ishyirwaho rya EBM, ibikoresho bya POS, kwimura amakuru y’iduka cyangwa imirimo ya buri munsi.",
                location: "AHO TURI",
                phone: "TELEFONI",
                email: "EMAIL",
                marqueePos: "POS",
                marqueeEbm: "EBM",
                marqueeInventory: "Stock",
                marqueeReports: "Raporo",
                marqueeExpenses: "Ibisohoka",
                marqueeMultistore: "Amaduka menshi",
                marqueeReceipts: "Inyemezabuguzi",
                marqueeSupport: "Ubufasha",
                marqueeSetup: "Gushyiraho iduka",
                marqueeInsights: "Amakuru y’ubucuruzi",
                marqueeCare: "Kwita ku mukiriya"
            },
            requestPos: {
                eyebrow: "Saba POS",
                title: "Tubwire ibyo iduka ryawe rikeneye",
                description: "Sangiza amakuru y’uko ukora, ikipe yacu igufashe guhitamo imikorere ya HovaStore POS ikubereye.",
                stepBusiness: "Amakuru y’ubucuruzi",
                stepSetup: "Gushyiraho POS",
                stepFollowUp: "Gukurikirana n’ikipe",
                openForm: "Fungura form",
                cardEyebrow: "Gushyiraho HovaStore POS",
                cardTitle: "Umwanya wo gusaba",
                profileTitle: "Umwirondoro w’iduka",
                profileDetail: "Izina, aho riherereye, TIN na branch",
                workflowTitle: "Uko kugurisha bikorwa",
                workflowDetail: "Telefoni, mudasobwa, printer n’inyemezabuguzi",
                supportTitle: "Uburyo bwo kugufasha",
                supportDetail: "Uko ikipe yacu yakuvugisha",
                nextStep: "Intambwe ikurikira",
                modalReady: "Fungura form igihe witeguye",
                modalTitle: "Saba HovaStore POS",
                modalSubtitle: "Uzuza form, ikipe yacu izagukurikirana."
            },
            guide: {
                eyebrow: "Ubuyobozi",
                title: "Igitabo cya HovaStore",
                description: "Amabwiriza arambuye agufasha gukoresha neza HovaStore POS.",
                viewFull: "Reba igitabo cyose",
                commonQuestions: "Ibibazo bikunze kubazwa",
                pdfGuide: "Igitabo PDF",
                workflowTitle: "Menya imikorere yose ya HovaStore",
                workflowDescription: "Fungura igitabo ubone ibisobanuro ku ishyirwaho, kugurisha, ibisohoka, raporo n’imirimo ya POS ya buri munsi.",
                itemStores: "Gukora no kuyobora amaduka",
                itemSales: "Kwandika igurisha n’inyemezabuguzi",
                itemExpenses: "Gukurikirana ibisohoka na raporo",
                itemConfidence: "Gukoresha HovaStore POS wizeye",
                openPreview: "Fungura igitabo",
                modalTitle: "Ibitabo bya HovaStore",
                modalSubtitle: "Reba igitabo cya PDF muri HovaStore."
            },
            faq: {
                eyebrow: "Ibibazo",
                title: "Ibibazo bya HovaStore",
                description: "Ibisubizo byihuse ku ishyirwaho, RRA EBM, stock, inyemezabuguzi, ubufasha n’imirimo ya POS ya buri munsi.",
                supportEyebrow: "Ubufasha",
                supportTitle: "Ukeneye ubufasha?",
                supportDescription: "Niba ikibazo cyawe kitari hano, ikipe yacu yagufasha kwimura amakuru, gushyiraho, printer, EBM n’imikorere y’iduka.",
                contactSupport: "Vugisha ubufasha"
            }
        }
    }

};

i18n
    .use(HttpApi)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        fallbackLng: 'en', // Default language
        debug: true,
        interpolation: {
            escapeValue: false, // React already escapes values
        },
        detection: {
            order: ['querystring', 'cookie', 'localStorage', 'navigator', 'htmlTag'],
            caches: ['cookie', 'localStorage'],
        },
    });

export default i18n;
